import React, { useState, useEffect, useRef } from 'react'
import './PurchaseOrdersPage.css'
import { useSelector, useDispatch } from "react-redux"
import MenuBar from "../../components/MenuBar"
import Header from "../../components/Header"
import { useNavigate } from "react-router-dom"
import LoadingSpinner from '../../components/placeholders/LoadingSpinner'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import httpClient from '../../components/helpers/httpClient'
import Label from '../../components/Label'
import DropDownFilter from '../../components/DropDownFilter'
import { updateFilters } from '../../store/purchaseOrdersPage'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import { formatDate } from '../../components/helpers/formatDate'
import { purchaseOrderStatusOptions, purchaseOrderStatusLabelColors } from '../../assets/data/data'
import { FaFileExcel } from "react-icons/fa"
import { toast } from "react-toastify"
import TableSelectCheckbox from '../../components/TableSelectCheckbox'
import ExportButton from '../../components/UI/ExportButton.jsx'
import { EXPORT_LIMIT, flattenExportRows } from '../../components/helpers/exportUtils'

function PurchaseOrdersPage() {
  const [purchaseOrders, setPurchaseOrders] = useState([])
  const [loadingPurchaseOrders, setLoadingPurchaseOrders] = useState(false)
  const [totalPages, setTotalPages] = useState(0)
  const [limit, setLimit] = useState(25)
  const [statuses, setStatuses] = useState([
    "draft",
    "for signature",
    "signed",
    "rejected",
    "cancelled",
  ])
  const [selectedPOs, setSelectedPOs] = useState([])

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const saveButton = useRef(null)
  const filters = useSelector(state => state.purchaseOrdersPage.filters)

  const fetchPurchaseOrders = async () => {
    setLoadingPurchaseOrders(true)
    // Construct query string from filters
    const queryParams = new URLSearchParams({
      page: filters.page,
      limit: limit,
      ...filters.statusFilter && { status: filters.statusFilter },
      ...filters.searchWord && { search: filters.searchWord },
      ...filters.sortFilter && { sort: filters.sortFilter },
      ...filters.sortAscending !== undefined && { ascending: filters.sortAscending }
    }).toString();

    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/purchase-orders?${queryParams}`)
    setPurchaseOrders(response.data.purchaseOrders || [])
    setTotalPages(response.data.total_pages || 0)
    setLoadingPurchaseOrders(false)
  }

  const prepareExportData = async () => {
    const queryParams = new URLSearchParams({
      page: 1,
      limit: EXPORT_LIMIT,
      ...filters.statusFilter && { status: filters.statusFilter },
      ...filters.searchWord && { search: filters.searchWord },
      ...filters.sortFilter && { sort: filters.sortFilter },
      ...filters.sortAscending !== undefined && { ascending: filters.sortAscending }
    }).toString()

    const response = await httpClient.get(import.meta.env.VITE_API_URL + `/api/purchase-orders?${queryParams}`)

    return flattenExportRows(response.data?.purchaseOrders || [])
  }

  useEffect(() => {
    fetchPurchaseOrders()
  }, [])

  useEffect(() => {
    fetchPurchaseOrders()
    setSelectedPOs([]) // Clear selections when filters change
  }, [filters])

  const handleOnClickRow = (poID) => {
    navigate(`/purchase-orders/${poID}`)
  }

  const handlePagination = (event) => {
    const element = event.currentTarget
    if (element.classList.contains("grayed-out")) {
      return
    }
    const type = element.dataset.type
    const updatedFilters = { ...filters }
    switch (type) {
      case "previous":
        updatedFilters["page"] = filters.page - 1
        dispatch(updateFilters(updatedFilters))
        break
      case "next":
        updatedFilters["page"] = filters.page + 1
        dispatch(updateFilters(updatedFilters))
        break
    }
  }

  const handleOnClickDropdownItem = (e, id = null, idList) => {
    let value = e.currentTarget.textContent
    const newFiltersObj = { ...filters }
    switch (idList) {
      case 'purchase_orders_filter--status':
        newFiltersObj.statusFilter = value
        break
    }
    newFiltersObj.page = 1
    dispatch(updateFilters(newFiltersObj))
  }

  const handleSearchWord = (event) => {
    const updatedFilters = { ...filters }
    updatedFilters["searchWord"] = event.target.value
    updatedFilters["page"] = 1
    dispatch(updateFilters(updatedFilters))
  }

  const handleClearSearchBar = () => {
    if (filters.searchWord === "") {
      return
    } else {
      const updatedFilters = { ...filters }
      updatedFilters["searchWord"] = ""
      updatedFilters["page"] = 1
      dispatch(updateFilters(updatedFilters))
    }
  }

  const handleSetSortFilter = (event) => {
    const innerHTML = event.currentTarget.innerHTML
    const updatedFilters = { ...filters }
    switch (innerHTML) {
      case "PO Number":
        if (filters.sortFilter === "po_number") {
          updatedFilters["sortAscending"] = !filters.sortAscending
        } else {
          updatedFilters["sortFilter"] = "po_number"
          updatedFilters["sortAscending"] = true
        }
        break
      case "Customer":
        if (filters.sortFilter === "customer") {
          updatedFilters["sortAscending"] = !filters.sortAscending
        } else {
          updatedFilters["sortFilter"] = "customer"
          updatedFilters["sortAscending"] = true
        }
        break
      case "Unit":
        if (filters.sortFilter === "unit") {
          updatedFilters["sortAscending"] = !filters.sortAscending
        } else {
          updatedFilters["sortFilter"] = "unit"
          updatedFilters["sortAscending"] = true
        }
        break
      case "Project":
        if (filters.sortFilter === "project") {
          updatedFilters["sortAscending"] = !filters.sortAscending
        } else {
          updatedFilters["sortFilter"] = "project"
          updatedFilters["sortAscending"] = true
        }
        break
      case "Status":
        if (filters.sortFilter === "status") {
          updatedFilters["sortAscending"] = !filters.sortAscending
        } else {
          updatedFilters["sortFilter"] = "status"
          updatedFilters["sortAscending"] = true
        }
        break
    }
    updatedFilters["page"] = 1
    dispatch(updateFilters(updatedFilters))
  }

  const handleSelectPO = (poId) => {
    setSelectedPOs(prev => {
      if (prev.includes(poId)) {
        return prev.filter(id => id !== poId)
      } else {
        return [...prev, poId]
      }
    })
  }

  const handleSelectAll = () => {
    if (selectedPOs.length === purchaseOrders.length) {
      setSelectedPOs([])
    } else {
      setSelectedPOs(purchaseOrders.map(po => po.id))
    }
  }

  const handleGenerateCombinedExcel = async () => {
    if (selectedPOs.length === 0) {
      toast.warning("Please select at least one purchase order")
      return
    }

    try {
      const url = import.meta.env.VITE_API_URL + `/api/purchase-orders/excel/multiple?po_ids=${selectedPOs.join(',')}`

      // Request Excel as a blob so we can detect JSON errors and show a toast instead of navigating
      const response = await httpClient.get(url, { responseType: 'blob' })
      const contentType = response.headers['content-type']

      // If backend returned JSON instead of Excel, parse and show error toast
      if (contentType && contentType.includes('application/json')) {
        try {
          const text = await response.data.text()
          const errorData = JSON.parse(text)
          toast.error(errorData?.message || 'Failed to generate Excel file')
        } catch (e) {
          toast.error('Failed to generate Excel file')
        }
        return
      }

      // Otherwise, treat it as a real Excel file and trigger download
      const blob = new Blob(
        [response.data],
        { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
      )
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = `Combined_PO_${new Date().toISOString().split('T')[0]}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)

      toast.success(`Excel generated for ${selectedPOs.length} purchase order(s)`)
    } catch (error) {
      console.error('Error downloading Excel file:', error)
      toast.error('Failed to generate Excel file')
    }
  }

  return (
    <div className='template'>
      <Header>
        <div className="header__actions--left">
          <ExportButton
            data={prepareExportData}
            filename="purchase_orders_export"
            formats={['excel']}
            disabled={loadingPurchaseOrders}
          />
        </div>

        <div className="header__filters" id="purchase-orders__table-btns">
          {selectedPOs.length === 0 ? (
            <>
              <div className="search-bar" id="purchase-orders__search-bar--table" onClick={handleClearSearchBar}>
                <input
                  placeholder="Search purchase order..."
                  value={filters.searchWord}
                  onChange={handleSearchWord}
                />
                {filters.searchWord === "" ? <SearchIcon /> : <ClearIcon />}
              </div>
              <DropDownFilter
                idList="purchase_orders_filter--status"
                placeholderText="Filter on status"
                listArray={purchaseOrderStatusOptions}
                onClickItem={handleOnClickDropdownItem}
                filters={filters}
                value={filters.statusFilter}
                filter={"statusFilter"}
                setValue={updateFilters}
              />
            </>
          ) : (
            <button
              className='icon-button orange'
              title={`Generate combined Excel for ${selectedPOs.length} PO(s)`}
              onClick={handleGenerateCombinedExcel}
            >
              <FaFileExcel />
            </button>
          )}
        </div>

      </Header>

      <MenuBar />

      <div className="template__body" id='body-purchase-orders'>
        <button
          className="floating__button--bottom-right"
          id="add--btn"
          onClick={() => navigate('/purchase-orders/new')}
        />

        <div className={`body__section`} id='all-purchase-orders-table'>
          <div className='table'>
            <div className='table__header-6'>
              <TableSelectCheckbox
                checked={selectedPOs.length === purchaseOrders.length && purchaseOrders.length > 0}
                onChange={handleSelectAll}
                isHeader={true}
              />
              <div className='table__header--field' onClick={handleSetSortFilter}>PO Number</div>
              <div className='table__header--field' onClick={handleSetSortFilter}>Created on</div>
              <div className='table__header--field' onClick={handleSetSortFilter}>Unit</div>
              <div className='table__header--field' onClick={handleSetSortFilter}>Project</div>
              <div className='table__header--field' onClick={handleSetSortFilter}>Status</div>
            </div>
            <div className='table__rows_container'>
              {loadingPurchaseOrders ? (
                <LoadingSpinner />
              ) : (
                purchaseOrders.map((po, index) => {
                  return (
                    <div
                      className='table__row-6'
                      key={index}
                      onClick={() => handleOnClickRow(po.id)}
                      data-po-id={po.id}
                    >
                      <TableSelectCheckbox
                        checked={selectedPOs.includes(po.id)}
                        onChange={() => handleSelectPO(po.id)}
                      />
                      <div className='table__row--field'>{po.id || "N/A"}</div>
                      <div className='table__row--field'>{formatDate(po.timestamp) || "N/A"}</div>
                      <div className='table__row--field'>{po.unit_id.unit_name || "N/A"}</div>
                      <div className='table__row--field'>{po.unit_id.building_id.project_id.project_commercial_name || "N/A"}</div>
                      <div className='table__row--field'>
                        {po.status ?
                          <Label
                            title={po.status}
                            labelColor={purchaseOrderStatusLabelColors[po.status]}
                          /> : null}
                      </div>
                    </div>
                  )
                })
              )}
            </div>
            <div className="pagination">
              <ArrowBackIosIcon
                className={`pagination__btn ${filters.page === 1 ? "grayed-out" : ""}`}
                onClick={handlePagination}
                data-type="previous"
                id="prevPage"
              />
              <ArrowForwardIosIcon
                className={`pagination__btn ${filters.page === totalPages ? "grayed-out" : ""}`}
                onClick={handlePagination}
                data-type="next"
                id="nextPage"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PurchaseOrdersPage
